import React, { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Phone,
  MessageSquare,
  Clock,
  ChevronRight,
  Filter,
  ArrowDownLeft,
  ArrowUpRight,
} from "lucide-react"
import { format } from "date-fns"
import { formatTimestamp, parseApiTimestamp } from "@/lib/datetime"
import { CallStatusBadge } from "@/components/ui/status-badge"
import {
  PatientCommunicationHistoryResponse,
  CommunicationHistoryItem,
  SMSConversationRecord,
} from "@/lib/types"

interface PatientCallsProps {
  patientId: string
  communicationHistory?: PatientCommunicationHistoryResponse | null
  loading?: boolean
  onPlayRecording?: (callId: string) => void
  onViewTranscript?: (callId: string) => void
}

export function PatientCalls({
  patientId,
  communicationHistory,
  loading = false,
  onPlayRecording,
  onViewTranscript,
}: PatientCallsProps) {
  const [filter, setFilter] = useState<"all" | "call" | "sms">("all")
  const [campaignFilter, setCampaignFilter] = useState<string>("all")
  const location = useLocation()

  // Get the current URL for the return link
  const returnUrl = `${location.pathname}${location.search}`

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-48" />
        </div>
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    )
  }

  const timeline = communicationHistory?.timeline || []
  const smsConversations = communicationHistory?.sms_conversations || []
  const summary = communicationHistory?.summary

  // Get unique strategy names for campaign filter
  const campaigns = Array.from(
    new Set(timeline.filter((c) => c.strategy_name).map((c) => c.strategy_name))
  )

  // Filter timeline items
  const filteredTimeline = timeline.filter((item) => {
    // Exclude canceled calls
    if (item.call_status === "canceled" || item.call_status === "cancelled") return false
    if (filter !== "all" && item.type !== filter) return false
    if (campaignFilter !== "all" && item.strategy_name !== campaignFilter) return false
    return true
  })

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${String(secs).padStart(2, "0")}`
  }

  const getDirectionIcon = (direction: string) => {
    return direction === "inbound" ? (
      <ArrowDownLeft className="h-3 w-3" />
    ) : (
      <ArrowUpRight className="h-3 w-3" />
    )
  }

  // Format scheduled time with timezone for pending calls
  const formatScheduledTime = (timestamp: string) => {
    const date = parseApiTimestamp(timestamp)
    if (!date) return "Scheduled"
    const timeZoneAbbr = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop()
    const time = format(date, "h:mm a")
    return `Scheduled for ${time} ${timeZoneAbbr}`
  }

  // Find SMS conversation details for an SMS timeline item
  const getSmsConversation = (id: string): SMSConversationRecord | undefined => {
    return smsConversations.find((conv) => conv.id === id)
  }

  const renderCallItem = (item: CommunicationHistoryItem) => (
    <Card key={item.id} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="mt-1">
            <Phone className="h-5 w-5 text-info" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {item.strategy_name || "Call"}
                </span>
                {item.call_status && (
                  item.call_status === "pending" || item.call_status === "scheduled" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                      {formatScheduledTime(item.timestamp)}
                    </span>
                  ) : (
                    <CallStatusBadge status={item.call_status} />
                  )
                )}
              </div>
              <span className="text-sm text-muted-foreground shrink-0">
                {formatTimestamp(item.timestamp, "MMM d, h:mm a")}
              </span>
            </div>

            <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
              <span className="flex items-center gap-1">
                {getDirectionIcon(item.direction)}
                {item.direction === "inbound" ? "Inbound" : "Outbound"}
              </span>
              {item.duration_seconds && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDuration(item.duration_seconds)}
                  </span>
                </>
              )}
              {item.call_type && (
                <>
                  <span>•</span>
                  <span>{item.call_type}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/calls/${item.id}`} state={{ returnUrl }}>
                  View Details
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  const renderSmsItem = (item: CommunicationHistoryItem) => {
    const conversation = getSmsConversation(item.id)

    return (
      <Card key={item.id} className="overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="mt-1">
              <MessageSquare className="h-5 w-5 text-success" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {item.strategy_name || "SMS Conversation"}
                  </span>
                  {item.call_status === "pending" || item.call_status === "scheduled" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                      {formatScheduledTime(item.timestamp)}
                    </span>
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      {item.message_count || conversation?.message_count || 0} messages
                    </Badge>
                  )}
                </div>
                <span className="text-sm text-muted-foreground shrink-0">
                  {formatTimestamp(item.timestamp, "MMM d, h:mm a")}
                </span>
              </div>

              {(item.last_message || conversation?.last_message) && (
                <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                  {item.last_message || conversation?.last_message}
                </p>
              )}

              {/* Show recent messages if available */}
              {conversation && conversation.messages.length > 0 && (
                <div className="mt-3 space-y-2 border-l-2 border-muted pl-3">
                  {conversation.messages.slice(-3).map((msg, idx) => (
                    <div key={idx} className="text-sm">
                      <span className={`font-medium ${msg.direction === "outbound" ? "text-info" : "text-success"}`}>
                        {msg.direction === "outbound" ? "Sent" : "Received"}:
                      </span>{" "}
                      <span className="text-muted-foreground">{msg.content}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      {summary && (
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{summary.total_calls} calls</span>
          <span>•</span>
          <span>{summary.total_sms_conversations} SMS conversations ({summary.total_sms_messages} messages)</span>
          {summary.last_contact_date && (
            <>
              <span>•</span>
              <span>Last contact: {formatTimestamp(summary.last_contact_date, "MMM d, yyyy")}</span>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="call">Calls</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {campaigns.length > 0 && (
          <Select
            value={campaignFilter}
            onValueChange={setCampaignFilter}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campaigns</SelectItem>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign} value={campaign!}>
                  {campaign}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Communication List */}
      {filteredTimeline.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Phone className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">No communication history</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTimeline.map((item) =>
            item.type === "call" ? renderCallItem(item) : renderSmsItem(item)
          )}
        </div>
      )}
    </div>
  )
}

export default PatientCalls
